import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleInitGame, handleInitFromReplayLink, handleGetBalanceStrategy, handleGetBalance } from "../../service";
import { symbolData } from "../../SymbolData";
import { loadAssets } from "./assetsSlice";
import { loadSounds, resetSoundSettings } from "./soundsSlice";
import { rgsPlay, rgsBuyFeature } from "./spinSlice";
import StorageManager from "../../utils/StorageManager";
import { DEV_SIMULATION_ENABLED, DEV_SIMULATION_ROUND } from "../../devSimulation";
import { FEATURES_CONFIG } from "../../featuresConfig";
import {
  openInfoPopup,
  closeIntroPopup,
  setUserInterfaceDisabled,
} from "./userInterfaceSlice";
import {
  setEnterRound,
  setEndedRound,
  startReplaySession,
  endReplaySession,
  fetchReplayInit,
} from "./replaySlice";

import { resetUISettings } from "./settingsSlice";

import { isStandaloneReplayMode, getStandaloneReplayUrl } from "../../utils/launcherOptions";

// Everything a replay spin overwrites in this slice (see rgsPlay.fulfilled).
// Snapshotted when a replay session starts, restored when it ends, so the live
// game comes back exactly as the player left it. bet/balance/displayedBalance
// are absent on purpose — replay never writes those (RGS returns them as
// undefined), so there is nothing to protect.
const REPLAY_SNAPSHOT_FIELDS = [
  "currentRound",
  "gameMode",
  "featureState",
  "features",
  "availableActions",
  "flowState",
  "freeRounds",
  "potProgress",
  "lastCommand",
];

// Async thunk for initializing the game and fetching initial data
export const initializeGame = createAsyncThunk(
  "gameSettings/initializeGame",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Standalone replay link (docs/replay-history-spec.md §0, Stage 9) —
      // this page was opened via a shared "Copy link" URL, not the normal
      // game entry. Confirmed live (2026-08-10): window.__OPTIONS__.api and
      // .play_token are both empty in this mode, so handleInitGame()'s own
      // missing-fields check would throw immediately if called — there is
      // no real session to validate/restore at all, so skip straight to
      // the replay boot instead of STEP 1/2 below.
      if (isStandaloneReplayMode()) {
        const replayUrl = getStandaloneReplayUrl();
        const result = await handleInitFromReplayLink(replayUrl);

        // Same as the live path below — non-fatal, asset/sound loading is
        // unaffected by which branch got us here.
        try {
          await dispatch(loadAssets()).unwrap();
        } catch (assetError) {
          console.warn("[initializeGame] loadAssets failed, continuing:", assetError);
        }
        try {
          await dispatch(loadSounds()).unwrap();
        } catch (soundError) {
          console.warn("[initializeGame] loadSounds failed, continuing:", soundError);
        }

        // No Intro ceremony for a standalone replay page — straight from
        // loader to the Enter modal (confirmed §0: skipping it costs no
        // load time, assets above already loaded in full either way).
        // sessionActive flips on immediately (not waiting for a Play click
        // like the in-game hot-swap path) so every §9 restriction — disabled
        // buttons, hidden History — applies for this page's entire
        // lifetime, not just once a round starts. `standalone: true` keeps
        // the balance-protection machinery (built for protecting a LIVE
        // wallet from replay data) out of the way, since there's no live
        // wallet here at all — state.balance below already legitimately
        // IS the replay balance. enterRound omits replayUrl on purpose:
        // the Enter modal's Play button only re-fetches when that field is
        // present, and here we're already fully booted into replay mode.
        // endedRound is pre-staged the same way handleEnterPlay does for
        // the in-game path — the Ended modal needs it the moment the round
        // finishes, and nothing else will ever set it in standalone mode.
        // `userInterfaceDisabled` starts true and is normally cleared by the
        // intro popup once it appears — but this mode has no intro popup at
        // all (it isn't even mounted, see Stage.js), so clear it here or the
        // Spin button stays permanently blocked with nothing left to unblock
        // it. Replay's own restrictions are enforced separately via
        // selectReplaySessionActive on each button.
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeIntroPopup());
        dispatch(startReplaySession({ standalone: true }));
        dispatch(setEnterRound({ bet: result.bet, time: null }));
        dispatch(setEndedRound({ bet: result.bet, time: null }));
        // Seed replaySlice's own bet/balance/step-cursor state the same way
        // the in-game path does. initFromReplayLink above already primed the
        // RGS object, but replaySlice drives the DISPLAY side — its
        // replayBalance is what BalanceDisplay shows and what applyReplayWin
        // adds each step's win onto. Without this the first playthrough has
        // no pre-win starting balance to reveal from, and every "Replay
        // Again" would resume from wherever the last one ended instead of
        // resetting. Same call "Replay Again" makes, so both take one code
        // path. Not awaited — the displays fall back to state.game until it
        // lands, and the RGS cursor it resets was already at zero.
        dispatch(fetchReplayInit());
        // NOTE: the Enter modal is deliberately NOT opened here. ReplayOverlay
        // is HTML mounted in App.js — outside the PIXI canvas — so it paints
        // OVER the loader, not under it. Opening it now (the loader still has
        // ~1s of settle + fade left) shows the popup floating on the loader's
        // black background, then the game appearing behind it. ReplayOverlay
        // opens it itself once `loaderFadedOut` is true, so the order is
        // loader → game → popup.

        return { result };
      }

      // STEP 1: Check session validity FIRST
      // Uses cache_player_id from window.__OPTIONS__ to detect player changes.
      // On their production server, the wrapper page sets cache_player_id before
      // loading the game. If it differs from what we stored, a new player is
      // using this browser — clear all stale localStorage data.
      const currentSessionToken = StorageManager.getCurrentSessionToken();
      const isValidSession = StorageManager.isSessionValid();

      if (!isValidSession) {
        // New session detected — clear all session-specific data
        StorageManager.clearSessionData();

        // Reset in-memory Redux state to defaults (slices loaded from
        // localStorage at module init, before clearSessionData runs)
        dispatch(resetSoundSettings());
        dispatch(resetUISettings());

        // Save new session token (cache_player_id)
        if (currentSessionToken) {
          StorageManager.saveSessionToken(currentSessionToken);
        }
      }

      // STEP 2: API call — validate session before downloading heavy assets
      // If this fails (expired session, server down, invalid token),
      // the user sees an error immediately without wasting bandwidth
      const result = await handleInitGame();

      // Session is valid — now load assets and sounds (non-fatal; loader error is init-only)
      try {
        await dispatch(loadAssets()).unwrap();
      } catch (assetError) {

      }
      try {
        await dispatch(loadSounds()).unwrap();
      } catch (soundError) {

      }

      // STEP 3: If free rounds are active, open InfoPopup immediately.
      // It will sit behind the IntroPopup and become visible when intro closes.
      // Skip when remaining === 0 (last round triggered bonus — popup is pointless)
      if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && result.freeRounds?.status === "active") {
        const performed = result.freeRounds.performed;
        const count = result.freeRounds.count;
        const remaining = count - performed;

        if (remaining > 0) {
          dispatch(openInfoPopup(
            performed === 0
              ? { key: "you_have_been_credited_with_free_rounds", data: { N: count } }
              : { key: "you_have_free_rounds_left", data: { N: remaining } }
          ));
        }
      }

      // Data is loaded - feature slices will react to universal state
      return { result };
    } catch (error) {
      // Nothing downstream of this thunk rejecting shows the real message
      // (loadingProgressSlice just flips to the generic "Something went
      // wrong" screen) — log it so it's actually visible in devtools when
      // debugging a boot failure, standalone replay or otherwise.
      console.error("[initializeGame] failed:", error);
      // Structured errors (e.g. synthesized 206 from FR cancellation) — pass through for error popup
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Heartbeat sync - calls init API to sync balance
 * Does NOT reload assets/sounds, only updates balance
 * CRITICAL: Only extracts balance to avoid unnecessary component re-renders
 * Note: Backend treats this as session keep-alive, but frontend only cares about balance
 */
export const heartbeatSync = createAsyncThunk(
  "gameSettings/heartbeatSync",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      // Return only balance to avoid triggering unnecessary updates
      return { balance: result.balance };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sync game state with server — calls init and returns balance + progress.
 * Reusable for any transition point that needs fresh server state.
 * Pass { syncPots: true } and/or { syncBalance: true } to control what updates.
 */
export const syncGameState = createAsyncThunk(
  "gameSettings/syncGameState",
  async (options = {}, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      return {
        balance: result.balance,
        progress: result.progress || null,
        syncPots: options.syncPots ?? false,
        syncBalance: options.syncBalance ?? false,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sync balance with server - calls provider's getBalance() endpoint
 * Lightweight: only fetches and updates balance, nothing else
 * Use after free spin outro, win animations, or anywhere balance needs server sync
 */
export const syncBalance = createAsyncThunk(
  "gameSettings/syncBalance",
  async (_, { rejectWithValue }) => {
    try {
      const { balance } = await handleGetBalance();
      return { balance };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sync free rounds total win with server — calls init and extracts freeRoundsTotalWin.
 * Lightweight: only updates FR displayedWin, nothing else.
 * Use after animated win counters finish during FR — server's value already includes current win.
 */
export const syncFreeRoundsWin = createAsyncThunk(
  "gameSettings/syncFreeRoundsWin",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      return { freeRoundsTotalWin: result.freeRoundsTotalWin ?? 0 };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Refresh game data - lightweight init without asset reloading
 * Used for error 205 (free rounds) to fetch updated game state
 * Does NOT reset spinSlice state, preserving spinComplete and autoplay
 * @param {Object} options - Options object
 * @param {boolean} options.useApiData - If true, uses fresh API data instead of stored data (for error recovery)
 * @param {boolean} options.ignoreFreeRoundsStorage - If true, ignores free rounds localStorage (for new batch activation)
 */
export const refreshGameData = createAsyncThunk(
  "gameSettings/refreshGameData",
  async (options = {}, { dispatch, rejectWithValue }) => {
    try {
      const { useApiData = false, ignoreFreeRoundsStorage = false } =
        typeof options === "boolean" ? { useApiData: options } : options;

      // API call only - RGS handles feature detection
      const result = await handleInitGame(useApiData, ignoreFreeRoundsStorage);

      // If free rounds are active after refresh, open InfoPopup
      // Skip when remaining === 0 (last round triggered bonus — popup is pointless)
      if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && result.freeRounds?.status === "active") {
        const performed = result.freeRounds.performed;
        const count = result.freeRounds.count;
        const remaining = count - performed;

        if (remaining > 0) {
          dispatch(openInfoPopup(
            performed === 0
              ? { key: "you_have_been_credited_with_free_rounds", data: { N: count } }
              : { key: "you_have_free_rounds_left", data: { N: remaining } }
          ));
        }
      }

      // Feature slices will react to universal state
      return { result };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

// Initial state for the game settings slice
const initialState = {
  token: "",
  balance: 0,
  displayedBalance: 0, // What the UI should show
  bet: 0,
  baseBet: 1, // Base bet for paytable calculations (default to 1 to avoid division by zero)
  bets: [],
  currency: "",
  currencyConfig: {
    subunits: 100, // Number of subunits per main currency unit (default: 100 for cents/dollars)
    exponent: 2, // Number of decimal places to display (default: 2 for "10.00")
    symbol: "", // Currency symbol (e.g., "$", "€", "FUN")
  },
  paytable: [],
  rtp: null, // RTP value from API init (e.g., 96.5)
  bonusBuyMultiplier: null, // Bonus buy cost multiplier — provided by API init (feature_multipliers.bonus_buy)
  bonusBuyExtraMultiplier: null, // Bonus buy level 2 cost multiplier — provided by API init
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
  },
  winningLines: [],
  status: "idle",
  error: null,
  errorCode: null, // Store error code from API (e.g., 301)
  errorDescription: null, // Store error description from API (e.g., "not_enough_money")
  // Universal state fields (provider-agnostic)
  gameMode: "base", // "base" | "feature" - universal game mode
  featureState: { type: null, phase: null, data: null }, // Universal feature state
  reloadState: { needsReload: false }, // Universal reload state

  // Legacy provider-specific fields (will be removed in Phase 11)
  availableActions: ["init", "spin"], // Track available actions from API (spin vs respin)
  flowState: "ready", // Track flow.state from API (ready, closed, freespins)
  features: null, // Track respin features: {respins_issued, respins_left, trigger_screen}
  lastCommand: null, // Track the command sent to API ("spin" or "respin")
  heartbeatError: {
    consecutiveFailures: 0,
    state: "none", // none, warning (3+ failures), critical (10+ failures)
  },
  freeRounds: null, // Casino-granted free rounds data from API
  jackpotMultipliers: null, // Raw multipliers from API: { mini: 15, minor: 50, major: 150, grand: 5000 }
  jackpotValues: { mini: 0, minor: 0, major: 0, grand: 0 }, // Jackpot display values (multiplier × bet)
  potProgress: null, // Zeus: POT stage data from API { D: { stage: N }, M: { stage: N }, C: { stage: N } }
  // Live round/feature state parked while a replay session borrows this slice.
  // null when not replaying. See the startReplaySession case in extraReducers.
  preReplaySnapshot: null,
};

// Helper: compute jackpot display values from bet and API multipliers
// When multipliers are provided (Zeus), uses API-driven values
// When multipliers are null (backward compat), falls back to symbolData
const computeJackpotValues = (bet, multipliers) => {
  if (!multipliers) {
    // Fallback: use symbolData values (backward compat for Noble Fruits)
    return {
      mini: (symbolData[27]?.value || 25) * (bet || 1),
      minor: (symbolData[28]?.value || 50) * (bet || 1),
      major: (symbolData[29]?.value || 150) * (bet || 1),
      grand: 0,
    };
  }
  return {
    mini: (multipliers.mini || 0) * (bet || 1),
    minor: (multipliers.minor || 0) * (bet || 1),
    major: (multipliers.major || 0) * (bet || 1),
    grand: (multipliers.grand || 0) * (bet || 1),
  };
};

// Create a slice for managing game settings
const gameSettingsSlice = createSlice({
  name: "gameSettings",
  initialState,
  reducers: {
    incrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex < state.bets.length - 1) {
        state.bet = state.bets[currentIndex + 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    decrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex > 0) {
        state.bet = state.bets[currentIndex - 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    setBet(state, action) {
      const betValue = action.payload;
      if (state.bets.includes(betValue)) {
        state.bet = betValue;
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            // This prevents overwriting good round data with H&W outro round (coins/blanks)
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);
      }
    },

    setBaseBet(state, action) {
      state.baseBet = action.payload || 1;
    },

    setBalance(state, action) {
      state.balance = Math.max(0, action.payload);
      state.displayedBalance = Math.max(0, action.payload); // Also update displayedBalance so UI shows the change
    },

    setDisplayedBalance(state, action) {
      state.displayedBalance = Math.max(0, action.payload);
    },

    updateDisplayedBalance(state, action) {
      // BALANCE STRATEGY: This reducer syncs displayedBalance after win animations complete
      // Behavior depends on provider's balance strategy:
      // - If serverIncludesWin (Redstone): Just sync to balance (already has win)
      // - If !serverIncludesWin (PopiPlay): Add win to balance, then sync
      //
      // For now, just syncing to balance works for Redstone
      // PopiPlay adapter will handle win addition in its own flow

      if (action.payload !== undefined) {
        state.displayedBalance = Math.max(0, action.payload);
      } else {
        // Sync displayedBalance to current balance
        state.displayedBalance = Math.max(0, state.balance);
      }
    },

    setAvailableActions(state, action) {
      state.availableActions = action.payload;
    },

    setFlowState(state, action) {
      state.flowState = action.payload;
    },

    setLastCommand(state, action) {
      state.lastCommand = action.payload;
    },

    restoreSavedBet(state) {
      // Restore bet from base game round (with session validation)
      if (!StorageManager.isSessionValid()) return;
      const baseGame = StorageManager.loadBaseGameRound();
      const savedBet = baseGame?.bet;
      if (savedBet && state.bets.includes(savedBet)) {
        state.bet = savedBet;
      }
    },

    /**
     * Clear featureState when returning to base game
     * Dispatched from SceneTransitionOverlay when exiting H&W
     */
    clearFeatureStateToBase(state) {
      state.featureState = { type: null, phase: null, data: null };
      state.gameMode = "base";
    },

    /**
     * Set featureState to free rounds when exiting H&W back to free rounds
     * Next spin will use "spin" with bonus options
     */
    setFeatureStateFromFreeRounds(state, action) {
      const { betValue, betLevel, count, performed, totalWin } = action.payload;
      state.featureState = {
        type: "freeRounds",
        phase: "active",
        data: {
          betValue: betValue ?? 0,
          betLevel: betLevel ?? null,
          count: count ?? 0,
          performed: performed ?? 0,
          totalWin: totalWin ?? 0,
        },
      };
      state.gameMode = "feature";
    },

    /**
     * Update reel symbols when expanding wilds replace them
     * This ensures the Redux state reflects the wild replacements
     * so that next spin reads correct symbols
     */
    updateReelSymbols(state, action) {
      const { reelIndex, symbolReplacements } = action.payload;

      if (state.currentRound?.reels?.[reelIndex]) {
        // symbolReplacements is an array of { position, symbolId }
        symbolReplacements.forEach(({ position, symbolId }) => {
          state.currentRound.reels[reelIndex][position] = symbolId;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Replay session entry: park the live round ──
      // A replay spin writes its recorded round straight into this slice
      // (see rgsPlay.fulfilled). Without stashing what was there first,
      // exiting replay leaves the live game showing the REPLAYED reels, and —
      // worse than the visual — a stale featureState/availableActions/
      // flowState/lastCommand, which getSpinParams reads to decide what the
      // next real spin sends. A replayed bonus round could leave the live
      // game asking the server for a respin it isn't in.
      //
      // Snapshot rather than clear, for the same reason as freeRoundsSlice:
      // the player may be mid-feature live and just watching an old round.
      // Only the first start snapshots — "Replay Again" dispatches this too,
      // and by then the slice holds replay data.
      .addCase(startReplaySession, (state) => {
        if (state.preReplaySnapshot) return;
        const snapshot = {};
        for (const field of REPLAY_SNAPSHOT_FIELDS) snapshot[field] = state[field];
        state.preReplaySnapshot = snapshot;
      })

      // ── Replay session exit: hand the live round back ──
      // Restores reels, feature state and the flow fields together, so the
      // player returns to precisely the board they left.
      .addCase(endReplaySession, (state) => {
        const snapshot = state.preReplaySnapshot;
        if (!snapshot) return;
        for (const field of REPLAY_SNAPSHOT_FIELDS) state[field] = snapshot[field];
        state.preReplaySnapshot = null;

        // ── The round coming back has ALREADY been celebrated ──
        //
        // The player watched their big win, opened the history, replayed some
        // other round, and is now put back where they were. Handing the round
        // back unmarked makes the win controller treat it as new and play the
        // whole tier chain a second time for money that was paid minutes ago.
        //
        // It has its own dedupe — `${roundId}-${winsHash}` in animatedRoundsRef
        // — which would have caught this by itself, except that the set is
        // cleared by the visual reset the replay runs on its way in and out.
        // Marking the ROUND survives that, because it travels with the data
        // rather than living in a ref, and it clears itself: the next real spin
        // brings a new round object without the flag.
        //
        // The win is still PROCESSED — the amount, the balance and the win row
        // all read the round as before. Only the presentation is skipped.
        if (state.currentRound) {
          state.currentRound = {
            ...state.currentRound,
            winAlreadyPresented: true,
          };
        }
      })

      // ── Replay init: the POT stages the replayed round STARTED on ──
      //
      // The init response carries `progress.pots`, the same field a live spin
      // reads. Written straight into `potProgress` so the orbs show the round
      // being watched rather than today's game — a replayed round with TODAY'S
      // pots behind it is two different rounds on screen at once.
      //
      // Safe to overwrite: `startReplaySession` has already parked the live
      // value in `preReplaySnapshot` (potProgress is in REPLAY_SNAPSHOT_FIELDS),
      // and `endReplaySession` puts it back. Same treatment the replayed steps
      // get — rgsPlay.fulfilled overwrites this field on every one of them.
      //
      // Only when the round actually reports pots, so a game without them, or a
      // response that omits the field, leaves the live value alone.
      .addCase(fetchReplayInit.fulfilled, (state, action) => {
        const pots = action.payload?.pots;
        if (pots) state.potProgress = pots;
      })

      .addCase(initializeGame.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoading = true;
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple assignment - no logic!
        // RGS has already handled all feature detection and storage operations
        state.token = result.token;
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);

        state.bet = result.bet;
        // Override bet with locked FR bet when free rounds are active
        if (result.freeRounds?.bet_value) {
          state.bet = result.freeRounds.bet_value;
        }
        state.baseBet = result.baseBet || 1;
        state.bets = result.bets;
        state.currency = result.currency;
        state.paytable = result.paytable || [];
        state.rtp = result.rtp || window.__OPTIONS__?.math?.rtp?.main || null;
        state.winningLines = result.winningLines || [];

        // Store currency configuration
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Set bonus buy multipliers if provided
        if (result.bonusBuyMultiplier !== undefined) {
          state.bonusBuyMultiplier = result.bonusBuyMultiplier;
        }
        if (result.bonusBuyExtraMultiplier !== undefined) {
          state.bonusBuyExtraMultiplier = result.bonusBuyExtraMultiplier;
        }

        // Store jackpot multipliers from API and compute display values
        state.jackpotMultipliers = result.jackpotMultipliers || null;
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);

        // Set current round if provided
        if (result.round) {
          state.currentRound = result.round;
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // ── Win replay balance withholding on reload ──
        // Server balance already includes the win, but the win animation /
        // completion popup will replay from localStorage. Subtract the pending
        // win from displayedBalance so the reveal feels authentic.
        // The close flow (finishAnimation / useFreeRoundsCompletion / SceneTransition)
        // syncs displayedBalance back to real balance when done.
        //
        // Priority: FR completion > feature outro > base game animated win
        // (FR total already includes individual spin wins — no double-counting)
        const isFeatureActive = state.gameMode === "feature";
        const savedFR = StorageManager.loadFreeRoundsRound();
        const frJustCompleted = savedFR && savedFR.performed >= savedFR.count && savedFR.win > 0;
        // FR still in progress — don't touch balance at all.
        // Individual FR spin wins don't affect displayedBalance mid-session.
        const frStillActive = savedFR && savedFR.performed < savedFR.count;

        if (frStillActive) {
          // Free Rounds in progress: balance stays as-is
        } else if (frJustCompleted) {
          // Free Rounds completed: total FR win replays via completion popups.
          // syncGameState in useFreeRoundsCompletion restores displayedBalance.
          state.displayedBalance = Math.max(0, state.displayedBalance - savedFR.win);
        } else if (!isFeatureActive) {
          // Base game: animated win (Big/Mega/SuperMega) replays on reload
          const savedBaseGame = StorageManager.loadBaseGameRound();
          if (savedBaseGame?.round?.isAnimatedWin && savedBaseGame.round.win > 0) {
            state.displayedBalance = Math.max(0, state.displayedBalance - savedBaseGame.round.win);
          }
        } else {
          // Feature (bonus outro): totalWin > 0 means last respin is done,
          // outro win animation will replay on reload
          const savedBonusGame = StorageManager.loadBonusGameRound();
          if (savedBonusGame?.totalWin > 0) {
            state.displayedBalance = Math.max(0, state.displayedBalance - savedBonusGame.totalWin);
          }
        }

        // Provider-specific fields (for backward compatibility during migration)
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.features = result.features || null;
        state.freeRounds = result.freeRounds || null;

        // Zeus: POT progress stages from API (used to restore pots on init)
        state.potProgress = result.progress?.pots || null;

        if (result.command) {
          state.lastCommand = result.command;
        }

        state.status = "succeeded";
        state.isLoading = false;
      })
      .addCase(initializeGame.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to initialize the game";
        state.isLoading = false;
      })

      // Listen for spin actions from spinSlice
      .addCase(rgsPlay.pending, (state, action) => {
        // Pessimistic update: Don't deduct balance until we get successful response
        // Just set status and clear previous wins

        // Clear all round data but KEEP reels for error recovery
        // GUARD: Don't touch base game currentRound during feature respins
        const isFeatureMode = state.gameMode === "feature";
        if (state.currentRound && !isFeatureMode) {
          state.currentRound.win = 0;
          state.currentRound.wins = [];
          state.currentRound.scatterCount = 0;
          state.currentRound.scatterPositions = [];
          state.currentRound.triggerWin = false;
          state.currentRound.winType = "";
          state.currentRound.isAnimatedWin = false;
          state.currentRound.anticipation = false;
          // DO NOT clear state.currentRound.reels - keep for error recovery!
        }

        state.error = null;
        state.status = "spinning";
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const {
          token,
          bet,
          balance,
          round,
          availableActions,
          flowState,
          features: featuresPayload,
          command,
          freeRounds: freeRoundsData,
          bonusCost,
          gameMode,
          featureState,
        } = action.payload;

        // Simple state updates - RGS has handled all storage operations
        if (token && token !== state.token) {
          state.token = token;
        }

        state.bet = bet !== undefined ? bet : state.bet;

        // BALANCE STRATEGY: Get provider-specific balance management strategy
        const balanceStrategy = handleGetBalanceStrategy();

        // GAME RULE: Determine if bet should be deducted (never during features)
        const isBaseGameSpin = gameMode === "base";

        // Check if free rounds just ended: was "played", now null (localStorage cleared)
        const wasFreeRoundsPlayed = state.freeRounds?.status === "played";
        const isFreeRoundsNowNull =
          freeRoundsData?.status === null || freeRoundsData === null;
        const freeRoundsJustEnded = wasFreeRoundsPlayed && isFreeRoundsNowNull;

        const isFreeRoundsActive =
          state.freeRounds?.status === "active" ||
          (state.freeRounds?.status === "played" && !freeRoundsJustEnded);

        // Apply balance strategy only for base game spins (not during features)
        const shouldDeductBet = isBaseGameSpin && !isFreeRoundsActive;

        if (shouldDeductBet && balanceStrategy.clientDeductsBet) {
          const deductionAmount =
            bonusCost !== undefined
              ? bonusCost
              : bet !== undefined
                ? bet
                : state.bet;
          state.balance = Math.max(0, state.balance - deductionAmount);
          state.displayedBalance = state.balance;
        }

        // Store the round — ONLY for base game spins
        // Feature games (RC, H&W) have their own round state in featureGameSettingsSlice
        const isFeatureSpin = gameMode === "feature" || state.gameMode === "feature";
        if (round && !isFeatureSpin) {
          state.currentRound = DEV_SIMULATION_ENABLED ? DEV_SIMULATION_ROUND : round;
        }

        // Store balance from API response
        // Strategy determines how balance is handled:
        // - If serverIncludesWin: balance already has win, sync after animations
        // - If !serverIncludesWin: balance is old, win will be added client-side after animations
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // displayedBalance stays at deducted amount until WinDisplay updates it
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = gameMode || "base";
        state.featureState = featureState || {
          type: null,
          phase: null,
          data: null,
        };

        // Provider-specific fields (for backward compatibility)
        state.features = featuresPayload || null;
        state.availableActions = availableActions || ["init", "spin"];
        state.flowState = flowState || "ready";
        state.freeRounds = freeRoundsData || null;

        // Zeus: update POT progress from spin response
        const progressPayload = action.payload.progress;
        if (progressPayload?.pots) {
          state.potProgress = progressPayload.pots;
        }

        if (command) {
          state.lastCommand = command;
        }

        state.status = "succeeded";
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        const errorData = action.payload;

        /* ============================================================
           DISABLED: Emergency Outcome Handling (Fake Spin Logic)

           Previously, RGS would generate fake non-winning combinations
           for errors. This logic is kept for backward compatibility but
           emergencyOutcome is no longer generated in RGS.js.

           All errors now freeze reels mid-spin via ReelControlContext.js
           instead of landing on fake patterns.
           ============================================================ */

        // Check for emergency outcome (legacy - no longer generated)
        if (errorData?.emergencyOutcome) {
          state.currentRound = {
            reels: errorData.emergencyOutcome.reels,
            wins: errorData.emergencyOutcome.wins || {},
            win: errorData.emergencyOutcome.win || 0,
            bet: errorData.emergencyOutcome.bet || state.bet,
          };
        }
        // No emergency outcome - reels freeze mid-spin instead

        // PESSIMISTIC UPDATE: No need to restore balance since we never deducted it
        // If API provides balance in error response, use it (for consistency)
        if (errorData?.balance !== undefined && errorData.balance !== null) {
          state.balance = Math.max(0, errorData.balance);
          state.displayedBalance = Math.max(0, errorData.balance);
        }
        // Otherwise, balance remains unchanged (as it should be)

        // Parse error response: {"errors":[{"code":301,"desc":"not_enough_money"}]}
        if (
          errorData?.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          const firstError = errorData.errors[0];
          state.errorCode = firstError.code;
          state.errorDescription = firstError.desc;
          state.error = `Error ${firstError.code}: ${firstError.desc}`;
        } else {
          state.error = errorData?.message || errorData || "Spin failed";
          state.errorCode = null;
          state.errorDescription = null;
        }

        state.status = "failed";
      })

      // Handle buy feature response
      .addCase(rgsBuyFeature.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
        state.status = "idle";
      })
      .addCase(rgsBuyFeature.rejected, (state, action) => {
        state.error = action.payload || "Buy feature failed";
        state.status = "failed";
      })

      // Handle heartbeat sync - update ONLY balance
      .addCase(heartbeatSync.fulfilled, (state, action) => {
        const { balance } = action.payload;

        // CRITICAL: Update ONLY balance to avoid unnecessary re-renders
        // Do NOT update any other fields even if they exist in the response
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // Also update displayedBalance so UI reflects the change immediately
          state.displayedBalance = Math.max(0, balance);
        }

        // Reset error state on successful heartbeat
        state.heartbeatError.consecutiveFailures = 0;
        state.heartbeatError.state = "none";
      })
      .addCase(heartbeatSync.rejected, (state, action) => {
        // Increment consecutive failure counter
        state.heartbeatError.consecutiveFailures += 1;

        // Update error state based on failure count
        if (state.heartbeatError.consecutiveFailures >= 10) {
          state.heartbeatError.state = "critical";
        } else if (state.heartbeatError.consecutiveFailures >= 3) {
          state.heartbeatError.state = "warning";
        }

        // Don't set game status to failed - heartbeat errors should not block gameplay
      })

      // Handle syncGameState - selectively updates balance and/or pot progress
      .addCase(syncGameState.fulfilled, (state, action) => {
        const { balance, progress, syncPots, syncBalance } = action.payload;

        if (syncBalance && balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }

        if (syncPots && progress?.pots) {
          state.potProgress = progress.pots;
        }
      })
      .addCase(syncGameState.rejected, () => {
        // Silent fail — pot/balance reset will happen on next spin/init
      })

      // Handle syncBalance - lightweight balance-only sync
      .addCase(syncBalance.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
      })
      .addCase(syncBalance.rejected, () => {
        // Silent fail - balance sync is not critical
      })

      // Handle refreshGameData - lightweight init for error 205 (free rounds)
      // Updates game data WITHOUT resetting spinSlice state or reloading assets
      .addCase(refreshGameData.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple state updates - RGS has handled feature detection
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);
        state.bet = result.bet;
        // Override bet with locked FR bet when free rounds are active
        if (result.freeRounds?.bet_value) {
          state.bet = result.freeRounds.bet_value;
        }
        state.bets = result.bets;

        // Update currency config if provided
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Update current round if provided (intro recovery: trigger_screen + coin wins)
        if (result.round) {
          state.currentRound = result.round;
        }
        // Paylines layout — needed for win controller matchHorizontalWinningLines
        if (result.winningLines) {
          state.winningLines = result.winningLines;
        }

        // CRITICAL: Update featureState/gameMode so first free round spin sends correct request
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // Provider-specific fields
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.freeRounds = result.freeRounds || null;

        // Update jackpot multipliers and recompute display values
        state.jackpotMultipliers = result.jackpotMultipliers || state.jackpotMultipliers;
        state.jackpotValues = computeJackpotValues(state.bet, state.jackpotMultipliers);

        // Clear error state
        state.error = null;
        state.errorCode = null;
        state.errorDescription = null;
        state.status = "idle";
      })
      .addCase(refreshGameData.rejected, (state, action) => {
        state.error = action.payload || "Failed to refresh game data";
        state.status = "failed";
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectToken = (state) => state.game.token;
export const selectBalance = (state) => state.game.balance;
export const selectDisplayedBalance = (state) => state.game.displayedBalance;
export const selectBet = (state) => state.game.bet;
export const selectBaseBet = (state) => state.game.baseBet || 1;
export const selectBets = (state) => state.game.bets;
export const selectCurrency = (state) => state.game.currency;
export const selectCurrencyConfig = (state) => state.game.currencyConfig;
export const selectPaytable = (state) => state.game.paytable;
export const selectRtp = (state) => state.game.rtp;
export const selectBonusBuyMultiplier = (state) =>
  state.game.bonusBuyMultiplier;
export const selectBonusBuyExtraMultiplier = (state) =>
  state.game.bonusBuyExtraMultiplier;
export const selectCurrentRound = (state) => state.game.currentRound;
export const selectWinningLines = (state) => state.game.winningLines;
export const selectScatterCount = (state) =>
  state.game.currentRound?.scatterCount || 0;
export const selectScatterPositions = (state) =>
  state.game.currentRound?.scatterPositions || [];
export const selectWinType = (state) => state.game.currentRound?.winType || "";
export const selectGameStatus = (state) => state.game.status;
export const selectGameError = (state) => state.game.error;
export const selectErrorCode = (state) => state.game.errorCode;
export const selectErrorDescription = (state) => state.game.errorDescription;

// Universal state selectors (provider-agnostic)
export const selectGameMode = (state) => state.game.gameMode;
export const selectFeatureState = (state) => state.game.featureState;
export const selectReloadState = (state) => state.game.reloadState;

// Legacy provider-specific selectors (will be removed in Phase 12)
export const selectAvailableActions = (state) => state.game.availableActions;
export const selectFlowState = (state) => state.game.flowState;
export const selectFeatures = (state) => state.game.features;
export const selectLastCommand = (state) => state.game.lastCommand;
export const selectHeartbeatError = (state) => state.game.heartbeatError;
export const selectFreeRounds = (state) => state.game.freeRounds;
export const selectJackpotValues = (state) => state.game.jackpotValues;
export const selectJackpotMultipliers = (state) => state.game.jackpotMultipliers;
export const selectPotProgress = (state) => state.game.potProgress;

/**
 * The LIVE pot stages, while a replay is borrowing this slice.
 *
 * `potProgress` holds the REPLAYED round's stages for as long as a session is
 * active — that is the point of the snapshot. Anything that has to show the
 * player's own pots mid-replay reads them here instead.
 *
 * null when no session is running, in which case `potProgress` is already live.
 */
export const selectPreReplayPotProgress = (state) =>
  state.game.preReplaySnapshot?.potProgress ?? null;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  incrementBet,
  decrementBet,
  setBalance,
  setBet,
  setBaseBet,
  setDisplayedBalance,
  updateDisplayedBalance,
  setAvailableActions,
  setFlowState,
  setLastCommand,
  restoreSavedBet,
  clearFeatureStateToBase,
  setFeatureStateFromFreeRounds,
  updateReelSymbols,
} = gameSettingsSlice.actions;

// Export the reducer to integrate into the Redux store
export default gameSettingsSlice.reducer;
